import type { LiveTransaction, TransactionSummary } from '@contract';
import { formatMoney } from '@sao/shared/money';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { createContext, useContext, useEffect, useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';
import { ExportButton } from '@/components/export-button';
import { PageHeader } from '@/components/page-header';
import { Money } from '@/components/primitives/money';
import { StatusPill } from '@/components/primitives/status-pill';
import { useSheet } from '@/components/sheet/use-sheet';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { formatDateTime } from '@/lib/format';
import { useKeysetList } from '@/lib/query';
import { readableValue } from '@/lib/readable';
import { settlementNote } from '@/lib/settlement';
import { kindLabel } from '@/lib/transactions';

const KINDS = [
  'transfer',
  'topup',
  'payout',
  'bill_payment',
  'payment',
  'fee',
  'adjustment',
  'reversal',
  'return',
  'release',
] as const;
const STATUSES = [
  'awaiting_approval',
  'pending',
  'posted',
  'failed',
  'reversed',
] as const;

const searchSchema = z.object({
  kind: z.enum(KINDS).optional(),
  status: z.enum(STATUSES).optional(),
  // An id or any reference: Sao's, the provider's or the ledger's.
  reference: z.string().trim().min(4).max(80).optional(),
  // A day, YYYY-MM-DD, included.
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});

/** Filters live in the URL, so a filtered view can be shared (decision 0007). */
export const Route = createFileRoute('/_app/transactions/')({
  validateSearch: searchSchema,
  component: TransactionsPage,
});

/** While the page is open it asks again this often. Every ask is a logged read. */
const LIVE_EVERY_MS = 10_000;
/** A transaction younger than this wears a "new" mark. */
const NEW_FOR_MS = 60_000;

const NowContext = createContext(Date.now());

/** The time, ticking every second, so ages on screen stay true. */
function useNow(): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** "12s ago", "4 min ago", "3 h ago"; beyond a day, the date. */
function ago(from: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - from) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
  return `${Math.floor(seconds / 86_400)} d ago`;
}

function WhenCell({ entry }: Readonly<{ entry: LiveTransaction }>) {
  const now = useContext(NowContext);
  const at = new Date(entry.created_at).getTime();
  return (
    <div title={formatDateTime(entry.created_at)}>
      <span className="block whitespace-nowrap font-medium tabular-nums">
        {entry.created_at.slice(11, 19)}
      </span>
      <span className="block whitespace-nowrap text-xs text-muted-foreground">
        {ago(at, now)}
      </span>
    </div>
  );
}

function WhatCell({ entry }: Readonly<{ entry: LiveTransaction }>) {
  const now = useContext(NowContext);
  const isNew = now - new Date(entry.created_at).getTime() < NEW_FOR_MS;
  const label = kindLabel(entry.type ?? entry.kind);
  return (
    <div>
      <button
        type="button"
        data-sheet-origin={`tx:${entry.id}`}
        className="flex items-center gap-2 rounded-xs text-left font-medium whitespace-nowrap"
      >
        {label}
        {isNew ? <Badge variant="info">new</Badge> : null}
      </button>
      {entry.channel && entry.channel !== label ? (
        <span className="block whitespace-nowrap text-xs text-muted-foreground">
          {entry.channel}
        </span>
      ) : null}
    </div>
  );
}

// No column sorts: the server hands rows over newest first and has no sort,
// and sorting only the rows already loaded would look like a ranking it is not.
const column = createColumnHelper<LiveTransaction>();
const columns = [
  column.display({
    id: 'when',
    header: 'When',
    cell: (info) => <WhenCell entry={info.row.original} />,
  }),
  column.display({
    id: 'kind',
    header: 'What',
    cell: (info) => <WhatCell entry={info.row.original} />,
  }),
  column.display({
    id: 'processor',
    header: 'Processed by',
    cell: (info) =>
      info.row.original.processor ?? (
        <span className="text-muted-foreground">Not sent yet</span>
      ),
  }),
  column.display({
    id: 'reference',
    header: 'Reference',
    cell: (info) => {
      const entry = info.row.original;
      const reference = entry.provider_reference ?? entry.client_reference;
      if (!reference) return <span className="text-muted-foreground">-</span>;
      return (
        <div className="min-w-0">
          <span className="block max-w-48 truncate font-mono text-xs">
            {reference}
          </span>
          {entry.client_reference && entry.client_reference !== reference ? (
            <span className="block max-w-48 truncate font-mono text-xs text-muted-foreground">
              {entry.client_reference}
            </span>
          ) : null}
        </div>
      );
    },
  }),
  column.display({
    id: 'customer',
    header: 'Customer',
    cell: (info) => {
      const entry = info.row.original;
      if (!entry.customer_id)
        return <span className="text-muted-foreground">System</span>;
      return (
        <div className="min-w-0">
          <span className="block max-w-48 truncate">
            {entry.customer_name ?? 'No name yet'}
          </span>
          <span className="block whitespace-nowrap font-mono text-xs text-muted-foreground">
            {entry.customer_phone ?? ''}
          </span>
        </div>
      );
    },
  }),
  column.display({
    id: 'status',
    header: 'Status',
    cell: (info) => {
      const entry = info.row.original;
      return (
        <div className="flex flex-col items-start gap-0.5">
          <StatusPill status={entry.status} />
          {settlementNote(entry) ? (
            <span className="max-w-56 truncate text-xs text-muted-foreground">
              {settlementNote(entry)}
            </span>
          ) : null}
          {entry.failure_code ? (
            <span className="max-w-56 truncate text-xs text-muted-foreground">
              {readableValue('failure_code', entry.failure_code)}
            </span>
          ) : null}
        </div>
      );
    },
  }),
  column.display({
    id: 'amount',
    header: 'Amount',
    cell: (info) => {
      const entry = info.row.original;
      return (
        <div className="text-right">
          <Money
            minor={entry.amount_minor}
            currency={entry.currency}
            className="block font-medium"
          />
          {entry.fee_minor > 0 ? (
            <span className="block whitespace-nowrap text-xs text-muted-foreground">
              fee <Money minor={entry.fee_minor} currency={entry.currency} />
            </span>
          ) : null}
        </div>
      );
    },
  }),
];

/**
 * While a record is open the list holds still, so no row moves under the
 * reader. Arrivals keep loading and wait behind the "N new" pill.
 */
function useHeldRows(rows: LiveTransaction[] | undefined, sheetOpen: boolean) {
  const [held, setHeld] = useState<LiveTransaction[] | null>(null);
  const heldIds = held ? new Set(held.map((row) => row.id)) : null;
  const arrivals =
    heldIds && rows ? rows.filter((row) => !heldIds.has(row.id)).length : 0;
  if (sheetOpen && held === null && rows) setHeld(rows);
  if (!sheetOpen && held !== null && arrivals === 0) setHeld(null);
  return {
    shown: held ?? rows,
    arrivals,
    showArrivals: () => setHeld(rows ?? null),
  };
}

function TransactionsPage() {
  const search = Route.useSearch();
  const [live, setLive] = useState(true);
  const now = useNow();
  const sheet = useSheet();
  const sheetOpen = sheet.top !== undefined;
  // A range is a look back, not the live feed.
  const ranged = Boolean(search.from || search.to || search.reference);
  const onNewest = !ranged;

  const {
    query: feed,
    first,
    rows,
    more,
  } = useKeysetList(
    'transactions.list',
    {
      kind: search.kind,
      status: search.status,
      reference: search.reference,
      from: search.from,
      to: search.to,
      limit: 50,
    },
    {
      rows: (page) => page.rows,
      refetchInterval: live && onNewest ? LIVE_EVERY_MS : false,
    },
  );

  const { shown, arrivals, showArrivals } = useHeldRows(rows, sheetOpen);

  const updatedSecondsAgo = feed.dataUpdatedAt
    ? Math.max(0, Math.round((now - feed.dataUpdatedAt) / 1000))
    : null;

  return (
    <>
      <PageHeader
        title="Transactions"
        description="Money moving on the app, newest first. Customers are masked, and every look is logged."
        actions={
          <div className="flex items-center gap-3">
            <LiveDot active={live && onNewest && !sheetOpen} />
            <span className="text-sm text-muted-foreground">
              {ranged
                ? 'The chosen filters'
                : liveLabel(live, updatedSecondsAgo, sheetOpen)}
            </span>
            {onNewest ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLive((was) => !was)}
              >
                {live ? 'Pause' : 'Resume'}
              </Button>
            ) : null}
            <ExportButton
              source="transactions.list"
              args={{
                kind: search.kind,
                status: search.status,
                reference: search.reference,
                from: search.from,
                to: search.to,
              }}
            />
          </div>
        }
      />
      {first ? <SummaryTiles summary={first.summary} /> : null}
      <Filters search={search} ranged={ranged} />
      {arrivals > 0 ? (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={showArrivals}>
            {arrivals} new · Show
          </Button>
        </div>
      ) : null}
      {feed.isError ? (
        <Alert variant="destructive">{feed.error.message}</Alert>
      ) : null}
      <NowContext.Provider value={now}>
        <DataTable
          columns={columns}
          data={shown}
          more={more}
          id="transactions"
          empty="No transactions match these filters."
          rowLabel={(row) => `Open transaction ${String(row.id).slice(0, 8)}`}
          onRowClick={(row, rowEl) =>
            sheet.open(
              { type: 'tx', id: row.id },
              { fromRowEl: rowEl.querySelector('button') ?? rowEl },
            )
          }
        />
      </NowContext.Provider>
    </>
  );
}

type Search = z.infer<typeof searchSchema>;

function Filters({
  search,
  ranged,
}: Readonly<{ search: Search; ranged: boolean }>) {
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
      <ReferenceSearch search={search} />
      <Select
        label="Status"
        className="w-full sm:w-48"
        value={search.status ?? ''}
        onChange={(event) =>
          void navigate({
            search: {
              ...search,
              status: (event.target.value || undefined) as
                | (typeof STATUSES)[number]
                | undefined,
            },
          })
        }
      >
        <option value="">Any</option>
        {STATUSES.map((status) => (
          <option key={status} value={status}>
            {status.replaceAll('_', ' ')}
          </option>
        ))}
      </Select>
      <Select
        label="Type"
        className="w-full sm:w-48"
        value={search.kind ?? ''}
        onChange={(event) =>
          void navigate({
            search: {
              ...search,
              kind: (event.target.value || undefined) as
                | (typeof KINDS)[number]
                | undefined,
            },
          })
        }
      >
        <option value="">Any</option>
        {KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {kindLabel(kind)}
          </option>
        ))}
      </Select>
      <DatePicker
        label="From"
        className="w-full sm:w-44"
        value={search.from ?? ''}
        onChange={(event) =>
          void navigate({
            search: {
              ...search,
              from: event.target.value || undefined,
            },
          })
        }
      />
      <DatePicker
        label="To"
        className="w-full sm:w-44"
        value={search.to ?? ''}
        onChange={(event) =>
          void navigate({
            search: {
              ...search,
              to: event.target.value || undefined,
            },
          })
        }
      />
      {ranged ? (
        <Button
          variant="ghost"
          onClick={() =>
            void navigate({
              search: {
                ...search,
                from: undefined,
                to: undefined,
              },
            })
          }
        >
          Clear dates
        </Button>
      ) : null}
    </div>
  );
}

/** Finds a transaction by its id or any reference; applied on Enter or Search. */
function ReferenceSearch({ search }: Readonly<{ search: Search }>) {
  const navigate = useNavigate({ from: Route.fullPath });
  const [text, setText] = useState(search.reference ?? '');
  const trimmed = text.trim();
  const valid = trimmed.length >= 4 && trimmed.length <= 80;
  const apply = (reference: string | undefined) =>
    void navigate({ search: { ...search, reference } });
  return (
    <form
      className="col-span-2 flex items-end gap-2 sm:w-96"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) apply(trimmed);
      }}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Label htmlFor="transaction-reference">Reference</Label>
        <Input
          id="transaction-reference"
          type="search"
          placeholder="Transaction id or any reference"
          maxLength={80}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      </div>
      <Button type="submit" variant="outline" disabled={!valid}>
        Search
      </Button>
      {search.reference ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setText('');
            apply(undefined);
          }}
        >
          Clear
        </Button>
      ) : null}
    </form>
  );
}

/** What the Live control says: running or paused. */
function liveLabel(
  live: boolean,
  updatedSecondsAgo: number | null,
  holding: boolean,
): string {
  if (!live) return 'Paused';
  if (holding) return 'Held while a record is open';
  return `Live · updated ${updatedSecondsAgo ?? 0}s ago`;
}

function LiveDot({ active }: Readonly<{ active: boolean }>) {
  return (
    <span className="relative flex size-2.5" aria-hidden="true">
      {active ? (
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-success-solid opacity-75" />
      ) : null}
      <span
        className={`relative inline-flex size-2.5 rounded-full ${active ? 'bg-success-solid' : 'bg-neutral-solid'}`}
      />
    </span>
  );
}

function SummaryTiles({ summary }: Readonly<{ summary: TransactionSummary }>) {
  const volume = Object.entries(summary.volume_minor)
    .map(([currency, minor]) => formatMoney(minor, currency))
    .join(' · ');
  const tiles = [
    { label: 'Last hour', value: summary.total },
    { label: 'Completed', value: summary.posted },
    { label: 'Failed', value: summary.failed, bad: summary.failed > 0 },
    { label: 'Waiting now', value: summary.waiting },
  ];
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((tile) => (
          <Card key={tile.label} className="gap-1">
            <span className="text-sm text-muted-foreground">{tile.label}</span>
            <span
              className={`text-2xl font-semibold tabular-nums ${tile.bad ? 'text-danger-ink' : ''}`}
            >
              {tile.value}
            </span>
          </Card>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">
        Completed volume in the last hour: {volume || 'none yet'}.
      </p>
    </div>
  );
}
